/**
 * Apple Health integration.
 *
 * Apple ships no web API for HealthKit, so the browser can't read Health at
 * all. The bridge is the Shortcuts app: a nightly automation on the phone reads
 * the samples and POSTs them to `/api/health`, and the app pulls them back down
 * here.
 *
 * The useful side effect is that anything which writes to Apple Health gets
 * picked up for free — including Technogym/MyFitness treadmill sessions, which
 * sync to Health from the gym's own app. One integration, all the sources.
 */

export interface HealthDay {
  date: string;
  steps?: number | null;
  activeEnergyKcal?: number | null;
  exerciseMinutes?: number | null;
  restingHr?: number | null;
}

export interface HealthWorkout {
  id: string;
  date: string;
  activity?: string | null;
  minutes?: number | null;
  distanceKm?: number | null;
  energyKcal?: number | null;
  avgHr?: number | null;
  source?: string | null;
}

export interface HealthPayload {
  days: HealthDay[];
  workouts: HealthWorkout[];
}

export const DEFAULT_ENDPOINT = '/api/health';

/**
 * HealthKit activity names → our cardio types.
 *
 * Matched loosely and case-insensitively because the exact string varies by
 * what recorded it: a Technogym treadmill, an Apple Watch and a third-party app
 * all spell the same activity differently.
 */
const ACTIVITY_MAP: { match: RegExp; cardioId: string }[] = [
  { match: /treadmill/i, cardioId: 'incline_walk' },
  { match: /stair|step/i, cardioId: 'stairmaster' },
  { match: /elliptical|cross.?train/i, cardioId: 'elliptical' },
  { match: /row/i, cardioId: 'rower' },
  { match: /\bski\b/i, cardioId: 'ski_erg' },
  { match: /cycl|bike|spin/i, cardioId: 'steady_bike' },
  { match: /swim/i, cardioId: 'swimming' },
  { match: /interval|hiit/i, cardioId: 'hiit_bike' },
  { match: /run|jog/i, cardioId: 'outdoor_run' },
  { match: /walk|hik/i, cardioId: 'outdoor_walk' },
];

/**
 * Workouts we deliberately ignore.
 *
 * Strength training is what you're already logging set by set — importing it as
 * a cardio entry would double-count the session and pollute your weekly cardio
 * totals.
 */
const IGNORED = /strength|weight|lifting|functional|core train|yoga|pilates|stretch/i;

export function mapActivityToCardioType(
  activity: string | null | undefined,
  source?: string | null,
): string | null {
  const haystack = `${activity ?? ''} ${source ?? ''}`.trim();
  if (!haystack) return null;
  if (IGNORED.test(haystack)) return null;

  for (const entry of ACTIVITY_MAP) {
    if (entry.match.test(haystack)) return entry.cardioId;
  }
  return null;
}

export interface HealthConfig {
  endpoint: string;
  token: string;
}

function endpointUrl(config: HealthConfig, since?: string): string {
  const base = config.endpoint.trim() || DEFAULT_ENDPOINT;
  const url = new URL(base, window.location.origin);
  if (since) url.searchParams.set('since', since);
  return url.toString();
}

export class HealthError extends Error {}

/** Pulls everything recorded on or after `since`. */
export async function fetchHealth(config: HealthConfig, since?: string): Promise<HealthPayload> {
  if (!config.token.trim()) {
    throw new HealthError('No token set. Paste the same token you put in Cloudflare.');
  }

  let response: Response;
  try {
    response = await fetch(endpointUrl(config, since), {
      headers: { Authorization: `Bearer ${config.token.trim()}` },
      cache: 'no-store',
    });
  } catch {
    // Offline is the common case here, not a broken setup.
    throw new HealthError('Could not reach the server. Are you online?');
  }

  if (response.status === 401) {
    throw new HealthError('Token rejected. It must match HEALTH_TOKEN in Cloudflare exactly.');
  }

  // A Pages project serves the app's index.html for any route it doesn't
  // recognise, so a missing or undeployed Function comes back as HTML with a
  // perfectly happy 200. Parsing that as JSON gives "Unexpected token '<'",
  // which tells you nothing. Check the content type and say what's wrong.
  const contentType = response.headers.get('Content-Type') ?? '';
  if (!contentType.includes('json')) {
    throw new HealthError(
      "That URL returned a web page instead of data — the /api/health function isn't deployed yet. Check the Setup guide.",
    );
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new HealthError(body?.error ?? `Server returned ${response.status}.`);
  }

  let data: HealthPayload;
  try {
    data = (await response.json()) as HealthPayload;
  } catch {
    throw new HealthError('The server sent something that was not valid data.');
  }
  return {
    days: Array.isArray(data.days) ? data.days : [],
    workouts: Array.isArray(data.workouts) ? data.workouts : [],
  };
}

/**
 * Real daily activity, averaged over the last fortnight.
 *
 * Beats the Settings dropdown, which asks you to self-assess how active you are
 * — something people are famously bad at. Real step counts remove the guess
 * from your calorie target.
 */
export function averageSteps(days: HealthDay[], window = 14): number | undefined {
  const withSteps = days
    .filter((d) => typeof d.steps === 'number' && d.steps! > 0)
    .slice(0, window);
  if (withSteps.length === 0) return undefined;
  const total = withSteps.reduce((sum, d) => sum + (d.steps ?? 0), 0);
  return Math.round(total / withSteps.length);
}

/**
 * Maps average daily steps onto the activity multiplier the calorie maths uses.
 * Thresholds follow the usual sedentary/light/moderate/active banding.
 */
export function activityLevelFromSteps(
  steps: number,
): 'sedentary' | 'light' | 'moderate' | 'active' {
  if (steps < 5000) return 'sedentary';
  if (steps < 8000) return 'light';
  if (steps < 12000) return 'moderate';
  return 'active';
}

export function stepsFor(days: HealthDay[], date: string): number | undefined {
  const day = days.find((d) => d.date === date);
  return typeof day?.steps === 'number' ? day.steps : undefined;
}
